import React from "react";
import { BrowserRouter as Router, Route, Routes, Navigate } from "react-router-dom";
import Unauthorized from "./pages/Unauthorized";
import Login from "./pages/Auth/Login";
import ForgotPassword from "./pages/Auth/ForgotPassword";
import Signup from "./pages/Auth/Signup";
import Pricing from "./pages/Public/Pricing";
import { AuthProvider, useAuth } from "./context/AuthContext";
import { FeedbackProvider } from "./context/FeedbackContext";
import AdminLayout from "./layouts/AdminLayout";
import { ReactQueryProvider } from "./lib/react-query";

import { TenantProvider } from "./context/TenantContext";

// Protected route wrapper component
const ProtectedAdminRoute: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
};

// Smart root redirect based on user role
const RootRedirect: React.FC = () => {
  const { isAuthenticated, isLoading, roles } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-gray-600">Loading...</div>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  const isSuper = (roles.includes("Super_Admin") || roles.includes("Super Admin") || roles.includes("super_admin")) && sessionStorage.getItem("is_impersonating") !== "true";
  return <Navigate to={isSuper ? "/super-admin" : "/admin/dashboard"} replace />;
};

const App: React.FC = () => {
  return (
    <ReactQueryProvider>
      <AuthProvider>
        <TenantProvider>
          <FeedbackProvider>
            <Router>
            <Routes>
              {/* Authentication Routes */}
              <Route path="/login" element={<Login />} />
              <Route path="/signup" element={<Signup />} />
              <Route path="/pricing" element={<Pricing />} />
              <Route path="/forgot-password" element={<ForgotPassword />} />

              {/* Super Admin Platform Routes - Protected */}
              <Route
                path="/super-admin/*"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout />
                  </ProtectedAdminRoute>
                }
              />

              {/* Tenant Admin Routes - Protected */}
              <Route
                path="/admin/*"
                element={
                  <ProtectedAdminRoute>
                    <AdminLayout />
                  </ProtectedAdminRoute>
                }
              />

              {/* Unauthorized Access */}
              <Route path="/unauthorized" element={<Unauthorized />} />

              {/* Default Route - Redirect to super admin or dashboard based on role */}
              <Route path="/" element={<RootRedirect />} />

              {/* 404 Route */}
              <Route path="*" element={<h1 className="text-center mt-20">404 - Page Not Found</h1>} />
            </Routes>
          </Router>
        </FeedbackProvider>
      </TenantProvider>
    </AuthProvider>
  </ReactQueryProvider>
  );
};

export default App;

